import {
	type DraggableItem,
	type DroppablePayloadBase,
	dropTargetForElements,
} from "@msuite/ui/dnd";
import { useDisclosure } from "@msuite/ui/hooks";
import {
	flip,
	offset,
	shift,
	useFloating,
	useHover,
	useInteractions,
} from "@msuite/ui/libs/floating";
import { Portal } from "@msuite/ui/portal";
import { cn } from "@msuite/ui/utils";
import React, {
	type PointerEvent as ReactPointerEvent,
	type ReactNode,
	useEffect,
	useRef,
	useState,
} from "react";
import { useCapacityCalendarContext } from "../capacity-calendar";
import { CapacityPlannerBarHandle } from "./capacity-planner-bar-handle";

/** How far a bar may travel along the day columns, in days. Both ends inclusive. */
export interface DayShiftRange {
	min: number;
	max: number;
}

/** Props Interface */
interface CapacityPlannerBarProps {
	gradient: string | null;
	/**
	 * How much of the row the bar takes. `full` is a figure somebody works with — a ticket's own
	 * work, a crew's days. `slim` is a summary of the rows underneath, drawn thin so a board of them
	 * reads as context rather than as the loudest thing on the screen.
	 */
	thickness?: "full" | "slim";
	/** Drawn as a thinner strip directly beneath the main bar, for a second reading of the days. */
	underlayGradient?: string | null;
	/**
	 * The whole window the work was given, as inclusive indices into the board's own. Drawn as a rail
	 * behind the work with a limiter at each end, so the row says how long there is to do the work in
	 * without a second bar competing with the one that says how much of it there is.
	 */
	windowBounds?: { from: number; to: number } | null;
	/**
	 * Makes the main bar draggable along the day columns. Called once on release with how many days
	 * it moved, never with zero. Absent leaves the bar inert, which is what every row but a
	 * ticket's wants.
	 */
	onDayShift?: (deltaDays: number) => void;
	/**
	 * The travel the caller will accept. The bar stops dead at these, so the drag cannot show a
	 * position the caller is only going to refuse.
	 */
	dayShiftRange?: DayShiftRange;
	/**
	 * Hatches the bar. Nobody being on the work is what a planner scans for, so it is the one state
	 * the block itself has to say without anything being read — who *is* on it is a caption's job,
	 * and a bar this thin has no room for a name.
	 *
	 * Only the hatching: what colour unstaffed work is drawn in belongs to whoever builds the
	 * gradient, because a filter here would grey the handles standing on top of it as well.
	 *
	 * A row with no notion of staffing leaves it alone, which is what keeps the hatching meaning one
	 * thing wherever it appears.
	 */
	isUnstaffed?: boolean;
	/**
	 * Fades the whole bar. Set while something is being dragged that this bar could not take, so the
	 * bars that *can* take it are the ones left at full strength — a reader with a crew in hand
	 * sees where it may land before trying. The bar stays a drop target either way; `canDrop` is
	 * what refuses the drop, this only says so in advance.
	 */
	isDimmed?: boolean;
	/**
	 * What the hatching is clipped to. The bar's own gradient is the obvious answer and the wrong
	 * one wherever a day is drawn at less than full strength: a mask reads alpha, so that day would
	 * hatch at the stop's own opacity instead of not at all. Absent falls back to the gradient,
	 * which is right for a bar drawn in one flat colour.
	 */
	hatchMask?: string | null;
	/**
	 * What may be dropped onto the bar. Given together with `handleOnDrop`, the block becomes a drop
	 * target — what a drop then means is the caller's, the same bargain `onDayShift` makes.
	 */
	dropAccepts?: Array<DraggableItem>;
	handleOnDrop?: (payload: DroppablePayloadBase) => void;
	/**
	 * Whether this particular thing may land here, asked while it is still in the air. A target that
	 * refuses shows no indicator and takes no drop, so a crew that cannot be scheduled here never
	 * looks as though it can — the alternative is inviting the drop and then rejecting it.
	 */
	canDrop?: (source: { type: string; item: unknown }) => boolean;
	/**
	 * The cells a drop applies to, as inclusive indices into the window. The drag highlight is drawn
	 * over exactly these — a row-wide highlight suggests the whole calendar is a target when only
	 * the ticket's own days are.
	 */
	dropHighlight?: { from: number; to: number } | null;
	/**
	 * Shown floating over the work while the pointer is on it, and nowhere else.
	 *
	 * On hover rather than drawn beside the bar: a row is eight pixels of colour on a calendar, and
	 * a caption standing next to every one of them is the loudest thing on the board — while the
	 * question it answers is one a reader asks of a single ticket at a time.
	 */
	hoverContent?: ReactNode;
	/**
	 * Which days the work occupies, as inclusive indices into the window. This is the box the resize
	 * handles sit in and the box the hover is asked of — hovering the row is not hovering the bar.
	 */
	workBounds?: { from: number; to: number } | null;
	/**
	 * Makes the work's two edges draggable. Called once on release with which edge moved and how far
	 * it travelled — zero included, so the caller can drop its preview. What an edge moving means is
	 * the caller's, the same bargain `onDayShift` makes.
	 */
	onResize?: (edge: "start" | "end", deltaDays: number) => void;
	/** How far each edge may travel, in days. A handle stops dead at these. */
	resizeRange?: { start: DayShiftRange; end: DayShiftRange };
	/**
	 * Makes the window's two ends draggable, reported the same way `onResize` reports the work's.
	 *
	 * The limiters already standing at those ends become the grips, rather than a second pair of
	 * handles appearing beside them: the mark that says where the window stops is the thing a reader
	 * would reach for to move it.
	 */
	onWindowResize?: (edge: "start" | "end", deltaDays: number) => void;
	/** How far each end of the window may travel, in days. */
	windowResizeRange?: { start: DayShiftRange; end: DayShiftRange };
	/**
	 * Puts a break on one day of the work, or takes it off again. Called with the column's index
	 * into the window.
	 *
	 * The marker is revealed with the resize handles rather than standing on the bar: a row is a few
	 * pixels of colour, and a control on every day of every ticket would be the loudest thing on the
	 * board for something asked of one day at a time.
	 */
	onToggleBreak?: (columnIndex: number) => void;
	/**
	 * The columns a marker is offered on. The caller's to decide, because only it knows which days
	 * are worked at all — breaking a Saturday says nothing, and offering the button there invites a
	 * click that changes nothing.
	 */
	breakableColumns?: number[];
	/** Which of those already carry a break, so the marker says which way it is about to go. */
	breakColumns?: number[];
}

/**
 * The block a row draws across its day columns.
 *
 * It is out of flow on purpose: an explicitly placed grid item would push the day cells into a
 * second row, because auto-placement skips the columns it occupies. Absolute over the day columns
 * leaves the cells where they are and lets the bar span them continuously.
 *
 * **Dragging moves the bar by whole days and reports only the distance.** What a day means — which
 * column it lands on, whether that column is one anybody works — is the caller's to decide, so this
 * knows the grid and nothing about tickets. What it does own is the feel: the bar stops at the
 * range it was given rather than sliding somewhere the caller will refuse, and it hands the
 * distance over the moment the pointer is released.
 */
export function CapacityPlannerBar({
	gradient,
	thickness = "full",
	hatchMask,
	underlayGradient,
	windowBounds,
	onDayShift,
	dayShiftRange,
	isUnstaffed,
	isDimmed,
	dropAccepts,
	handleOnDrop,
	dropHighlight,
	canDrop,
	hoverContent,
	workBounds,
	onResize,
	resizeRange,
	onWindowResize,
	windowResizeRange,
	onToggleBreak,
	breakableColumns,
	breakColumns,
}: CapacityPlannerBarProps) {
	/** Context */
	const { controller } = useCapacityCalendarContext();

	/** Hooks */
	const trackRef = useRef<HTMLDivElement>(null);
	const originRef = useRef<number | null>(null);
	// Measured once when the drag starts. Reading `clientWidth` while rendering answers from
	// whatever layout happens to be current, and on the first frame answers zero — the bar then
	// sits still through a drag that is plainly moving.
	const dayWidthRef = useRef(0);
	// The distance the commit reports. State drives the transform; this is what is read on release,
	// because a pointerup batched with the last pointermove would otherwise commit the previous
	// frame's distance.
	const shiftRef = useRef(0);

	// Callers pass an inline closure, so depending on it directly would tear the drop target down
	// and rebuild it on every render. The ref keeps the registration stable and still calls the
	// current handler.
	const dropHandlerRef = useRef(handleOnDrop);
	dropHandlerRef.current = handleOnDrop;
	// Held the same way and for the same reason: a predicate rebuilt every render would tear the
	// registration down with it.
	const canDropRef = useRef(canDrop);
	canDropRef.current = canDrop;

	const hoverDisclosure = useDisclosure();

	/** State */
	// Whole days, so the bar steps between columns while dragging rather than sliding between them.
	const [dayShift, setDayShift] = useState(0);
	const [isDropTarget, setIsDropTarget] = useState(false);
	// Where an edge currently stands while it is being dragged. The bar itself is a gradient over the
	// whole track and cannot be resized, so the prospective span is outlined instead and the caller
	// redraws once the release has been stored.
	const [resize, setResize] = useState<{
		span: "work" | "window";
		edge: "start" | "end";
		deltaDays: number;
	} | null>(null);

	// Anchored to the work itself, so the panel follows a two-day bar rather than the row it sits in.
	// Closed while either drag is running: a panel chasing the bar across the calendar hides the very
	// columns the drag is aiming at.
	const { refs, floatingStyles, context } = useFloating({
		open: hoverDisclosure.isOpen,
		onOpenChange: hoverDisclosure.setIsOpen,
		placement: "top",
		middleware: [offset(6), flip(), shift({ padding: 8 })],
	});
	const hover = useHover(context, { enabled: dayShift === 0 && !resize, move: false });
	const { getFloatingProps, getReferenceProps } = useInteractions([hover]);

	/** Effects */
	// The drag-and-drop primitive rather than the `Droppable` component, deliberately: that
	// component sets `content-visibility: auto` and forces `position: relative` on the element it
	// owns, and this track is absolutely positioned — under size containment its contents stop being
	// laid out and the bar disappears.
	const acceptKey = dropAccepts?.join(",") ?? "";
	useEffect(() => {
		const element = trackRef.current;
		const accepts = acceptKey ? acceptKey.split(",") : [];
		if (!element || !accepts.length) return;

		return dropTargetForElements({
			element,
			canDrop: ({ source }) => {
				if (!accepts.includes(source.data.type as string)) return false;
				const ask = canDropRef.current;
				if (!ask) return true;
				return ask({ type: source.data.type as string, item: source.data.item });
			},
			onDragEnter: () => setIsDropTarget(true),
			onDragLeave: () => setIsDropTarget(false),
			onDrop: (payload) => {
				setIsDropTarget(false);
				dropHandlerRef.current?.(payload);
			},
		});
	}, [acceptKey]);

	/** Functions */
	// Measured on demand rather than held: the columns are `1fr`, so a day is only as wide as the
	// board's current layout says, and reading it while rendering answers zero on the first frame.
	function getDayWidth() {
		const width = trackRef.current?.clientWidth ?? 0;
		return width / Math.max(controller.cells.length, 1);
	}

	function handleOnPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
		if (!onDayShift) return;
		event.preventDefault();
		dayWidthRef.current = getDayWidth();
		originRef.current = event.clientX;
		shiftRef.current = 0;
		event.currentTarget.setPointerCapture(event.pointerId);
	}

	function handleOnPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
		if (originRef.current === null || !dayWidthRef.current) return;
		const travelled = Math.round((event.clientX - originRef.current) / dayWidthRef.current);
		const clamped = dayShiftRange
			? Math.min(dayShiftRange.max, Math.max(dayShiftRange.min, travelled))
			: travelled;
		shiftRef.current = clamped;
		setDayShift(clamped);
	}

	function handleOnPointerUp(event: ReactPointerEvent<HTMLDivElement>) {
		if (originRef.current === null) return;
		originRef.current = null;
		event.currentTarget.releasePointerCapture(event.pointerId);

		const travelled = shiftRef.current;
		shiftRef.current = 0;
		// The transform goes back to nothing in the same commit the caller moves the row's own days,
		// so the bar never passes through its old position on the way to the new one.
		setDayShift(0);
		if (travelled !== 0) onDayShift?.(travelled);
	}

	/** Guard */
	if (!gradient && !underlayGradient && !windowBounds) return null;

	/** Computed */
	// Percentages of the day area, because the columns are `1fr` and have no width until the board
	// has been laid out. Without a range the highlight is the whole row, which is what a row with no
	// span of its own wants.
	const columnCount = Math.max(controller.cells.length, 1);
	const highlightBounds = dropHighlight
		? {
				left: `${(dropHighlight.from / columnCount) * 100}%`,
				width: `${((dropHighlight.to - dropHighlight.from + 1) / columnCount) * 100}%`,
			}
		: { left: 0, right: 0 };

	// The handles sit on the work's own two edges, so they are placed inside a box the size of the
	// work rather than against the whole day area. That box is also what the hover is asked of:
	// hovering the row is not hovering the bar, and handles that appear anywhere along the calendar
	// would read as belonging to the row rather than to this piece of work.
	const workBox = workBounds ? boxOf(workBounds, columnCount) : undefined;

	const windowBox = windowBounds ? boxOf(windowBounds, columnCount) : undefined;
	// Held to the columns there are. The travel a handle is allowed is measured in dates, and a
	// window may reach past either edge of the board — an outline drawn to a column that is not on
	// screen is scrollable overflow for as long as the drag lasts.
	const resizedBounds = resize?.span === "window" ? windowBounds : workBounds;
	const previewBounds =
		resize && resizedBounds
			? {
					from: clampToWindow(
						resizedBounds.from + (resize.edge === "start" ? resize.deltaDays : 0),
						columnCount,
					),
					to: clampToWindow(
						resizedBounds.to + (resize.edge === "end" ? resize.deltaDays : 0),
						columnCount,
					),
				}
			: null;
	const previewStyle = previewBounds
		? {
				left: `${(previewBounds.from / columnCount) * 100}%`,
				width: `${((previewBounds.to - previewBounds.from + 1) / columnCount) * 100}%`,
			}
		: undefined;

	/** Render */
	return (
		// The offset is the first column's own width, and each gradient's stops are per day with
		// colours from the data — neither has a Tailwind class to reach for.
		<div
			ref={trackRef}
			className={cn(
				"absolute inset-y-[20%] right-0 gap-px p-0! border-r-0! v-stack",
				thickness === "slim" && "justify-center",
			)}
			style={{ left: controller.constants.FIRST_COLUMN_WIDTH }}
		>
			{isDropTarget && (
				<div
					className="absolute -inset-y-1 rounded-[3px] ring-2 ring-accent-500 pointer-events-none"
					style={highlightBounds}
				/>
			)}
			{/* Behind everything, and thin: the window is context, not a second figure. The two ends
			    are marked, because a rail that simply stops reads as the drawing running out rather
			    than as the day the work has to be finished by.

			    Everything stays inside the window's own columns. A limiter drawn a few pixels outside
			    reads better where the work fills its whole window, but a window reaching the board's
			    last column then puts those pixels past the track and the whole board gains a
			    horizontal scrollbar. */}
			{windowBox && (
				<div
					className="absolute inset-y-0 pointer-events-none"
					style={windowBox}
				>
					<div className="absolute top-1/2 -translate-y-1/2 inset-x-0 h-[10%] bg-gray-400" />
					{!onWindowResize && (
						<React.Fragment>
							<div className="absolute top-1/2 -translate-y-1/2 h-1/2 left-0 w-[2px] rounded-full bg-gray-400" />
							<div className="absolute top-1/2 -translate-y-1/2 h-1/2 right-0 w-[2px] rounded-full bg-gray-400" />
						</React.Fragment>
					)}
				</div>
			)}
			{previewStyle && (
				<div
					className="absolute -inset-y-1 rounded-[3px] ring-2 ring-accent-500 pointer-events-none"
					style={previewStyle}
				/>
			)}
			<div
				className={cn(
					"w-full relative transition-opacity",
					thickness === "slim" ? "h-[30%]" : "flex-1",
					onDayShift && "cursor-grab active:cursor-grabbing touch-none select-none",
					isDimmed && "opacity-20",
				)}
				style={{
					...(gradient ? { backgroundImage: gradient } : undefined),
					// Pixels rather than a column count, because the columns are `1fr` and their width
					// is only known once the board has been laid out.
					...(dayShift
						? { transform: `translateX(${dayShift * dayWidthRef.current}px)` }
						: undefined),
				}}
				onPointerDown={handleOnPointerDown}
				onPointerMove={handleOnPointerMove}
				onPointerUp={handleOnPointerUp}
				onPointerCancel={handleOnPointerUp}
			>
				{/* Nobody is on this work. The hatching sits over the bar rather than replacing it, so
				    the row still says how much work there is and when, and it takes no pointer events
				    or it would swallow the drag the bar itself is listening for.

				    A gradient is its mask: transparent on every day the work does not cover, and a mask
				    reads alpha, so the hatching lands on exactly the days that are painted — weekends
				    cut out of the run included. A box over the work's first and last column cannot do
				    that, because the run is not continuous. The caller may hand a different one where
				    the bar draws a day at part strength, which would otherwise hatch at that day's
				    own opacity rather than not at all. */}
				{isUnstaffed &&
					(() => {
						const mask = hatchMask ?? gradient;
						if (!mask) return null;
						return (
							<div
								className="absolute inset-0 mid-stripes mix-blend-overlay opacity-25 pointer-events-none"
								style={{ maskImage: mask, WebkitMaskImage: mask }}
							/>
						);
					})()}
				{workBox && (
					// One box over the work, and the only thing on the row the pointer is asked about:
					// it anchors the hover panel and holds the two handles, which are hidden until the
					// pointer is on the work and held open for as long as an edge is being dragged —
					// the pointer leaves this box long before the drag does.
					<div
						className={cn(
							"absolute inset-y-0 transition-opacity",
							onResize && (resize ? "opacity-100" : "opacity-0 hover:opacity-100"),
						)}
						style={workBox}
						{...getReferenceProps({ ref: refs.setReference })}
					>
						{onToggleBreak &&
							workBounds &&
							breakableColumns?.map((index) => (
								// Small on purpose: the block underneath carries the drag that moves the
								// work, and a target the width of a column would swallow it.
								<button
									key={index}
									type="button"
									className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex items-center px-1 cursor-pointer"
									style={{
										left: `${((index - workBounds.from + 0.5) / Math.max(workBounds.to - workBounds.from + 1, 1)) * 100}%`,
									}}
									onPointerDown={(event) => event.stopPropagation()}
									onClick={(event) => {
										event.stopPropagation();
										onToggleBreak(index);
									}}
								>
									<span
										className={cn(
											"block size-1.5 rounded-full hover:opacity-80 active:opacity-50",
											breakColumns?.includes(index) ? "bg-red-500" : "bg-white/70",
										)}
									/>
								</button>
							))}
						{onResize && resizeRange && (
							<React.Fragment>
								<CapacityPlannerBarHandle
									edge="start"
									range={resizeRange.start}
									getDayWidth={getDayWidth}
									onPreview={(deltaDays) => setResize({ span: "work", edge: "start", deltaDays })}
									onCommit={(deltaDays) => {
										setResize(null);
										if (deltaDays !== 0) onResize("start", deltaDays);
									}}
								/>
								<CapacityPlannerBarHandle
									edge="end"
									range={resizeRange.end}
									getDayWidth={getDayWidth}
									onPreview={(deltaDays) => setResize({ span: "work", edge: "end", deltaDays })}
									onCommit={(deltaDays) => {
										setResize(null);
										if (deltaDays !== 0) onResize("end", deltaDays);
									}}
								/>
							</React.Fragment>
						)}
					</div>
				)}
			</div>
			{/* The limiters, over the block rather than under it. The block is the whole day area and
			    carries the drag that moves the work, so a limiter drawn beneath it never sees a
			    pointer — the grab lands on the bar and the work slides instead. The layer itself takes
			    no events, or it would shadow that drag across the window's whole width. */}
			{windowBox && onWindowResize && windowResizeRange && (
				<div
					className="absolute inset-y-0 pointer-events-none"
					style={windowBox}
				>
					<CapacityPlannerBarHandle
						edge="start"
						variant="limiter"
						range={windowResizeRange.start}
						getDayWidth={getDayWidth}
						onPreview={(deltaDays) => setResize({ span: "window", edge: "start", deltaDays })}
						onCommit={(deltaDays) => {
							setResize(null);
							if (deltaDays !== 0) onWindowResize("start", deltaDays);
						}}
					/>
					<CapacityPlannerBarHandle
						edge="end"
						variant="limiter"
						range={windowResizeRange.end}
						getDayWidth={getDayWidth}
						onPreview={(deltaDays) => setResize({ span: "window", edge: "end", deltaDays })}
						onCommit={(deltaDays) => {
							setResize(null);
							if (deltaDays !== 0) onWindowResize("end", deltaDays);
						}}
					/>
				</div>
			)}
			{/* A fifth of the block, so it reads as an annotation under the bar rather than a second
			    bar competing with it. Absent, the bar has the whole track — an empty strip would take
			    a fifth of the height off every row that draws only one figure. */}
			{underlayGradient && (
				<div
					className="w-full h-[20%] shrink-0"
					style={{ backgroundImage: underlayGradient }}
				/>
			)}
			{hoverContent && hoverDisclosure.isOpen && (
				<Portal>
					<div
						className="z-50 absolute"
						style={floatingStyles}
						{...getFloatingProps({ ref: refs.setFloating })}
					>
						<div className="card p-2 h-stack flex-wrap gap-1 shadow-lg">{hoverContent}</div>
					</div>
				</Portal>
			)}
		</div>
	);
}

/** Where a run of days sits along the day area, as percentages of it. */
function boxOf(bounds: { from: number; to: number }, columnCount: number) {
	return {
		left: `${(bounds.from / columnCount) * 100}%`,
		width: `${((bounds.to - bounds.from + 1) / columnCount) * 100}%`,
	};
}

/** A column index held to the ones the board is drawing. */
function clampToWindow(index: number, columnCount: number) {
	return Math.min(Math.max(index, 0), columnCount - 1);
}
